import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { registerLocaleData } from '@angular/common';
import localeSv from '@angular/common/locales/sv';
import localeEn from '@angular/common/locales/en-GB';
import localeNo from '@angular/common/locales/no';

import { StoreRouterConnectingModule, routerReducer } from '@ngrx/router-store';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { ProjectListModule } from './project-list/project-list.module';
import { AuthModule } from './auth/auth.module';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { getMagiCoreHttpLoader } from '@magicore/core';
import { environment } from '../environments/environment';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ActionReducer, MetaReducer, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { UserAccountModule } from './user-account/user-account.module';
import { constants } from './core/constants/globals';
import { ProjectModule } from './project/project.module';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { AcPaginatorIntl } from './core/services/ac-paginator-intl.service';
import { globalV3dSettings, Visualize3dModule } from '@magicore/visualize-3d';

// Reset state on logout
export function logoutReset(reducer: ActionReducer<any>): ActionReducer<any> {
  return (state, action) => {
    return reducer(action.type === '[Session] logout' ? undefined : state, action);
  };
}

export const metaReducers: MetaReducer[] = [logoutReset];

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    CoreModule,
    StoreModule.forRoot(
      {
        router: routerReducer
      },
      { metaReducers }
    ),
    StoreRouterConnectingModule.forRoot(),
    StoreDevtoolsModule.instrument({
      maxAge: 10, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: true
    }),
    EffectsModule.forRoot([]),
    ProjectListModule,
    ProjectModule,
    AuthModule,
    UserAccountModule,
    TranslateModule.forRoot({
      defaultLanguage: constants.uiDefaultLanguage,
      loader: {
        provide: TranslateLoader,
        deps: [HttpClient],
        useFactory: (httpClient: HttpClient) => {
          return getMagiCoreHttpLoader(httpClient, `${environment.apiUrl}`);
        }
      }
    }),
    Visualize3dModule.forRoot(`${environment.apiUrl}/visualize-3d`, environment.throwOnQpdErrors)
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: AcPaginatorIntl }, provideHttpClient(withInterceptorsFromDi())]
})
export class AppModule {
  constructor() {
    registerLocaleData(localeSv, 'sv-SE');
    registerLocaleData(localeNo, 'no');
    registerLocaleData(localeEn, 'en');

    // Custom font path for Visualize3D dimension lines
    globalV3dSettings.qmodelFontUrl = '/assets/fonts/roboto_medium_regular.json';
    globalV3dSettings.debug = true;
  }
}
